// Copyright (c) 2026, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.
import React, { useState, useEffect, Fragment, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import {
  AppConfig,
  roles,
  FORM_STATUS,
  COLUMN_NAMES,
  LINK_STATUS,
  FINANCE_RESULT,
  LEGAL_RESULT,
  REGION,
  RESELLER_FORM_STATUS,
  FILTERS
} from "@configs/admin-config";
import { useHistory } from "react-router-dom";
import MuiAlert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Box,
  Grid,
  Button,
  InputBase,
  Backdrop,
  CircularProgress,
  Snackbar,
  Typography,
  FormControlLabel,
  Checkbox,
  Menu,
  MenuItem,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NewResellerDialog from "../Dialog/NewResellerDialog";
import EditResellerDialog from "../Dialog/EditResellerDialog";
import { getAccessToken, checkIfRoleExists } from "../../../utils/oauth";
import TablePagination from "@mui/material/TablePagination";
import CachedIcon from "@mui/icons-material/Cached";
import IconButton from "@mui/material/IconButton";
import FileCopyIcon from '@mui/icons-material/FileCopy';
import EditIcon from '@mui/icons-material/Edit';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: 2,
    color: "white",
  },
  root: {
    padding: "2px 4px",
    display: "flex",
    alignItems: "center",
    border: "1px solid #AAA",
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: "4px",
  },
  accordionHeading: {
    fontSize: 14,
    fontWeight: 500,
  },
  accordionDetails: {
    display: "block",
  },
  searchGrid: {
    marginBottom: 20,
  },
  filterContainer: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    padding: "16px 0"
  },
  filterButton: {
    textTransform: "none",
    textAlign: "right",
    border: "1px solid #ccc",
    backgroundColor: "white",
    padding: "5px 20px",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    color: theme.palette.text.primary,
  },
  filterMenu: {
    marginTop: "8px",
  },
  filterMenuPaper: {
    maxHeight: "300px",
    marginTop: "42px",
    borderRadius: "5px",
  },
  checkBox: {
    fontSize: 9,
    textAlign: "center",
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    height: 28,
    margin: 4,
  },
  tableContainer: {
    marginTop: 20,
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: theme.palette.primary.light,
  },
  column: {
    cursor: "pointer",
    fontSize: 13,
    "&:hover": {
      // backgroundColor: theme.palette.primary.main,
      // color: "white",
      fontWeight: 600,
    },
  },
  tableRow1: {
    "&:hover": {
      backgroundColor: "#F5F5F5",
      cursor: "pointer",
    },
  },
  tableRow2: {
    "&:hover": {
      cursor: "not-allowed",
    },
  },
  primaryText: {
    color: theme.palette.primary.main,
    fontSize: 13,
    fontWeight: "bold",
  },
  secondaryText: {
    color: theme.palette.secondary.main,
    fontSize: 13,
    fontWeight: "bold",
  },
  plainText: {
    fontSize: 13,
    paddingLeft: 6,
    paddingRight: 6,
  },
  dateText: {
    fontSize: 12,
    paddingLeft: 6,
    paddingRight: 6,
  },
  button: {
    backgroundColor: "white",
    textTransform: "none",
    fontSize: 11,
  },
  addButton: {
    textTransform: "none",
    marginRight: "20px",
  },
  downloadButton: {
    textTransform: "none",
    whiteSpace: "nowrap",
  },
  dialog: {
    border: "1px solid black",
  },
  dialogActions: {
    paddingRight: 23,
    paddingTop: 15,
    paddingBottom: 20,
  },
  msgTypo: {
    paddingTop: 20,
    textAlign: "center",
    color: theme.palette.secondary.main,
  },
  infoMsg: {
    marginTop: 10,
    color: "grey",
  },
  errorMsg: {
    color: "red",
    fontSize: 12,
    marginBottom: 5,
  },
  actionButton: {
    textTransform: "none",
  },
  errorText: {
    color: "theme.palette.error.main",
    fontSize: 13,
    fontWeight: "bold",
  },
  tableRowExpired: {
    backgroundColor: "#FFCCCB",
    "&:hover": {
      backgroundColor: "#FFB8B8",
      cursor: "pointer",
    }
  },
  activeFilterChip: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.text.secondary}`,
    borderRadius: "16px",
    padding: "4px 12px",
    margin: "4px",
    fontSize: "13px",
    color: theme.palette.text.secondary,
  },
  activeFilterChipClose: {
    marginLeft: "8px",
    cursor: "pointer",
    fontSize: "10px",
  },
  menuItem: {
    paddingTop: "4px",
    paddingBottom: "4px",
  },
  filterLabel: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  resultChip: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    border: "1px solid",
    borderRadius: "16px",
    padding: "4px 12px",
    fontSize: "11px",
  },
  actionMenuItem: {
    padding: "12px 16px",
    minWidth: "200px",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  actionMenuIcon: {
    marginRight: "12px",
    color: theme.palette.text.secondary,
  },
  actionMenuText: {
    fontSize: "14px",
    color: theme.palette.text.primary,
  },
  actionMenuPaper: {
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    marginTop: "4px",
  }
}));

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const getFormStatusLabel = (formStatus, linkStatus) => {
  if (linkStatus === LINK_STATUS.INACTIVE) return "Inactive";
  if (formStatus === FORM_STATUS.SIGNED) return RESELLER_FORM_STATUS.COMPLETED;
  if (formStatus === FORM_STATUS.COMPLETED || formStatus === FORM_STATUS.EDIT_COMPLETED) return RESELLER_FORM_STATUS.PENDING_SIGNATURE;
  if (formStatus === FORM_STATUS.TR_EDIT_REQUESTED || formStatus === FORM_STATUS.FULL_EDIT_REQUESTED) return RESELLER_FORM_STATUS.REQUESTED_TO_EDIT;
  if (formStatus === FORM_STATUS.DRAFTED || formStatus === FORM_STATUS.TR_DRAFTED) return RESELLER_FORM_STATUS.DRAFTED;
  if (formStatus === FORM_STATUS.ACTIVE && linkStatus === LINK_STATUS.EXPIRED) return RESELLER_FORM_STATUS.EXPIRED;
  return RESELLER_FORM_STATUS.PENDING;
};

const Resellers = ({ id }) => {
  let query = useQuery();
  const { classes } = useStyles();
  const history = useHistory();

    
  const [resellers, setResellers] = useState([]);
  const [filteredResellers, setFilteredResellers] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snack, setSnackBar] = useState({ visibility: false, severity: "", message: "" });
  const [backdropOpen, setbackDrop] = useState(true);
  const [formRender, setFormRender] = useState(0);
  const [sortColumn, setSortColumn] = useState("Company Name");
  const [sortPriority, setSortPriority] = useState("DESC");
  const [priorityChanged, setPriorityChanged] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [copySnack, setCopy] = useState(false);
  const [isInactive, setInactive] = useState(false);
  const [editLinkId, setEditLinkId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editEmails, setEditEmails] = useState([]);
  const [editContactName, setEditContactName] = useState("");
  const [editChannelManagerEmail, setEditChannelManagerEmail] = useState("");
  const [formStatusAnchor, setFormStatusAnchor] = useState(null);
  const [financeApprovalAnchor, setFinanceApprovalAnchor] = useState(null);
  const [legalApprovalAnchor, setLegalApprovalAnchor] = useState(null);
  const [regionAnchor, setRegionAnchor] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formStatusFilters, setFormStatusFilters] = useState({
    // Signed is shown as 'Completed' in the dashboard
    signed: false,
    // Completed is shown as 'Pending Signature' in the dashboard
    completed: false,
    drafted: false,
    pending: false,
    requestedToEdit: false,
    expired: false,
  });
  const [financeApprovalFilters, setFinanceApprovalFilters] = useState({
    pending: false,
    approved: false,
    rejected: false,
    creatorPending: false,
    reviewerPending: false,
    approvalPending: false,
  });
  const [legalApprovalFilters, setLegalApprovalFilters] = useState({ 
    approved: false, 
    rejected: false, 
    pending: false 
  });

  const [regionFilters, setRegionFilters] = useState({
    northAmerica: false,
    latam: false,
    middleEast: false,
    anz: false,
    uk: false,
    eu: false,
    asia: false,
    africa: false,
    other: false,
  });

  const getResellers = async () => {
    const requestURL = AppConfig.baseUrl + AppConfig.partnerData;

    try {
      let response = await fetch(requestURL, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      });
      let body = await response.json();
      if (response.status === 200) {
        if (body) {
          let resellers = [];
          let links = [];
          if (body.partnerInfo.length > 0) {
            resellers = body.partnerInfo;
          }
          if (body.partnerLinkInfo.length > 0) {
            links = body.partnerLinkInfo;
          }
          let resellerLinks = [];

          links.map((link) => {
            const linkId = link.linkId;
            const reseller =
              resellers.filter((res) => res.linkId === linkId).length > 0
                ? resellers.filter((res) => res.linkId === linkId)[0]
                : {
                    companyId: "",
                    linkId: linkId,
                    companyName: link.companyName,
                    taxIdNumber: "-",
                    streetAddress: "-",
                    state: "-",
                    cityProvince: "-",
                    postalZipCode: "-",
                    country: link.country,
                    yearOfIncorporation: "-",
                    noOfEmployees: "-",
                    noOfCustomers: "-",
                    websiteLink: "-",
                    formStatus: "active",
                    financeResult: "pending",
                    legalResult: "pending",
                    region: link.region || "-",
                    channelManagerEmail: link.channelManagerEmail,
                    contactName: link.contactName || "-",
                    signedDocumentUpdatedOn: null,
                    legalResultUpdatedOn: null,
                    financeResultUpdatedOn: null,
                  };

            if (reseller) {
              link["reseller"] = reseller;
            }

            resellerLinks.push(link);
          });
          setResellers(resellerLinks);

          if (query.get("id")) {
            setFilteredResellers(
              sortRows(
                resellerLinks.filter((item) => item.linkId === query.get("id")),
                sortColumn
              )
            );
          } else {
            setFilteredResellers(
              sortRows(
                resellerLinks.filter((item) => item.status === LINK_STATUS.ACTIVE || item.status === LINK_STATUS.EXPIRED),
                sortColumn
              )
            );
          }
        } else {
          console.warn("Not retrieved any data from backend");
        }
      } else {
        setSnackBar({ visibility: true, severity: "error", message: "Something went wrong while retrieving data" });
      }

      setbackDrop(false);
      return null;
    } catch (error) {
      setSnackBar({ visibility: true, severity: "error", message: "Something went wrong while retrieving data" });
      setbackDrop(false);
      console.error(error);
      return error;
    }
  };

  useEffect(() => {
    setbackDrop(true);
    getResellers();
  }, [formRender]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, filteredResellers.length - page * rowsPerPage);

  const openSummary = (item) => {
    if (
      item.reseller.formStatus === FORM_STATUS.COMPLETED ||
      item.reseller.formStatus === FORM_STATUS.EDIT_COMPLETED ||
      item.reseller.formStatus === FORM_STATUS.SIGNED ||
      item.reseller.formStatus === FORM_STATUS.DRAFTED ||
      item.reseller.formStatus === FORM_STATUS.TR_DRAFTED ||
      item.reseller.formStatus === FORM_STATUS.PENDING ||
      item.reseller.formStatus === FORM_STATUS.TR_EDIT_REQUESTED ||
      item.reseller.formStatus === FORM_STATUS.FULL_EDIT_REQUESTED ||
      (item.reseller.formstatus === FORM_STATUS.PENDING && item.status === LINK_STATUS.EXPIRED)
    ) {
      history.push({
        pathname: AppConfig.urls.ADMIN + AppConfig.urls.RESELLER_DASHBOARD + item.reseller.companyId + "/profile",
        state: {
          tabName: "profile",
          encodeString: item.encodeString,
          isTradeReferenceEnabled: item.isTradeReferenceEnabled ?? false,
        },
      });
    } else {
      history.push({
        pathname: AppConfig.urls.ADMIN + AppConfig.urls.PENDING_PAGE + item.reseller.linkId,
        state: { tabName: "profile", item: item },
      });
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleSearch = (e) => {
    const input = e.target.value;
    setSearchInput(input);
    // let filteredSearch = resellers.filter(item=>(
    //     (item.reseller.companyName ? item.reseller.companyName.toLowerCase().includes(input.toLowerCase()):
    //         item.temp_company_name.toLowerCase().includes(input.toLowerCase())) ||
    //     item.email.toLowerCase().includes(input.toLowerCase()) ||
    //     (item.reseller.country.toLowerCase().includes(input.toLowerCase()))
    // ))
    // setSearchedResellers(filteredSearch)
  };

  const handleDownloadCsv = () => {
    const escape = (val) => {
      const str = val == null ? "" : String(val);
      const sanitized = /^[=+\-@]/.test(str) ? `'${str}` : str;
       return sanitized.includes(",") || sanitized.includes('"') || sanitized.includes("\n")
         ? `"${sanitized.replace(/"/g, '""')}"`
         : sanitized;
    };
    const headers = [
      "Company Name",
      "Contact Email",
      "Channel Manager Email",
      "Country",
      "Region",
      "Form Status",
      "Finance Approval",
      "Legal Approval",
    ];
    const rows = resellers.map((item) => [
      escape(item.reseller?.companyName),
      escape((item.emails || []).join("; ")),
      escape(item.channelManagerEmail),
      escape(item.reseller?.country),
      escape(item.reseller?.region),
      escape(getFormStatusLabel(item.reseller?.formStatus, item.status)),
      escape(item.reseller?.financeResult),
      escape(item.reseller?.legalResult),
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "resellers.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const openDialog = () => {
    setDialogOpen(true);
  };

  const handleCheckbox = (e) => {
    if (!e.target.checked) {
      setFormStatusFilters({
        signed: true,
        completed: true,
        drafted: true,
        pending: true,
        requestedToEdit: true,
        expired: true,
      });
      setFinanceApprovalFilters({
        pending: true,
        approved: true,
        rejected: true,
        creatorPending: true,
        reviewerPending: true,
        approvalPending: true,
      });
      setLegalApprovalFilters({
        approved: true,
        rejected: true,
        pending: true,
      });
      setRegionFilters({
        northAmerica: true,
        latam: true,
        middleEast: true,
        anz: true,
        uk: true,
        eu: true,
        asia: true,
        africa: true,
        other: true,
      });
    }

    setInactive(e.target.checked);
    if (e.target.checked) {
      setFilteredResellers(
        resellers.filter((item) => item.status === LINK_STATUS.INACTIVE)
      );
    } else {
      setFilteredResellers(
        resellers.filter(
          (item) =>
            item.status === LINK_STATUS.ACTIVE ||
            item.status === LINK_STATUS.EXPIRED
        )
      );
    }
  };

  const handleFormStatusFilters = (filterName) => {
    setFormStatusFilters((prev) => ({
      ...prev,
      [filterName]: !prev[filterName],
    }));
  };

  const handleFinanceApprovalFilter = (filterName) => {
    setFinanceApprovalFilters((prev) => ({
      ...prev,
      [filterName]: !prev[filterName],
    }));
  };

  const handleLegalApprovalHandler = (filterName) => {
    setLegalApprovalFilters((prev) => ({
      ...prev,
      [filterName]: !prev[filterName],
    }));
  };

  const handleRegionFilter = (filterName) => {
    setRegionFilters((prev) => ({
      ...prev,
      [filterName]: !prev[filterName],
    }));
  };

const sortRows = useCallback((array, sortColumn) => {
  if (sortPriority === "DESC") {
    if (sortColumn === COLUMN_NAMES.COMPANY_NAME) {
      return array.sort((a, b) =>
        a.reseller.companyName.toLowerCase() > b.reseller.companyName.toLowerCase()
          ? 1
          : b.reseller.companyName.toLowerCase() > a.reseller.companyName.toLowerCase()
          ? -1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.CONTACT_EMAIL) {
      return array.sort((a, b) =>
        a.emails[0].toLowerCase() > b.emails[0].toLowerCase()
          ? 1
          : b.emails[0].toLowerCase() > a.emails[0].toLowerCase()
          ? -1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.CHANNEL_MANAGER_EMAIL) {
      return array.sort((a, b) =>
        (a.channelManagerEmail?.toLowerCase() || "") > (b.channelManagerEmail?.toLowerCase() || "")
          ? 1
          : (b.channelManagerEmail?.toLowerCase() || "") > (a.channelManagerEmail?.toLowerCase() || "")
          ? -1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.COUNTRY) {
      return array.sort((a, b) =>
        a.reseller.country.toLowerCase() > b.reseller.country.toLowerCase()
          ? 1
          : b.reseller.country.toLowerCase() > a.reseller.country.toLowerCase()
          ? -1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.REGION) {
      return array.sort((a, b) =>
        a.reseller.region.toLowerCase() > b.reseller.region.toLowerCase()
          ? 1
          : b.reseller.region.toLowerCase() > a.reseller.region.toLowerCase()
          ? -1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.FORM_STATUS) {
      return array.sort((a, b) =>
        a.reseller.formStatus.toLowerCase() > b.reseller.formStatus.toLowerCase()
          ? 1
          : b.reseller.formStatus.toLowerCase() > a.reseller.formStatus.toLowerCase()
          ? -1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.FINANCE_APPROVAL) {
      return array.sort((a, b) =>
        a.reseller.financeResult.toLowerCase() > b.reseller.financeResult.toLowerCase()
          ? 1
          : b.reseller.financeResult.toLowerCase() > a.reseller.financeResult.toLowerCase()
          ? -1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.LEGAL_APPROVAL) {
      return array.sort((a, b) =>
        a.reseller.legalResult.toLowerCase() > b.reseller.legalResult.toLowerCase()
          ? 1
          : b.reseller.legalResult.toLowerCase() > a.reseller.legalResult.toLowerCase()
          ? -1
          : 0
      );
    } else {
      return array;
    }
  } else {
    if (sortColumn === COLUMN_NAMES.COMPANY_NAME) {
      return array.sort((a, b) =>
        a.reseller.companyName.toLowerCase() > b.reseller.companyName.toLowerCase()
          ? -1
          : b.reseller.companyName.toLowerCase() > a.reseller.companyName.toLowerCase()
          ? 1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.CONTACT_EMAIL) {
      return array.sort((a, b) =>
        a.emails[0].toLowerCase() > b.emails[0].toLowerCase()
          ? -1
          : b.emails[0].toLowerCase() > a.emails[0].toLowerCase()
          ? 1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.CHANNEL_MANAGER_EMAIL) {
      return array.sort((a, b) =>
        (a.channelManagerEmail?.toLowerCase() || "") > (b.channelManagerEmail?.toLowerCase() || "")
          ? -1
          : (b.channelManagerEmail?.toLowerCase() || "") > (a.channelManagerEmail?.toLowerCase() || "")
          ? 1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.COUNTRY) {
      return array.sort((a, b) =>
        a.reseller.country.toLowerCase() > b.reseller.country.toLowerCase()
          ? -1
          : b.reseller.country.toLowerCase() > a.reseller.country.toLowerCase()
          ? 1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.FORM_STATUS) {
      return array.sort((a, b) =>
        a.reseller.formStatus.toLowerCase() > b.reseller.formStatus.toLowerCase()
          ? -1
          : b.reseller.formStatus.toLowerCase() > a.reseller.formStatus.toLowerCase()
          ? 1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.FINANCE_APPROVAL) {
      return array.sort((a, b) =>
        a.reseller.financeResult.toLowerCase() > b.reseller.financeResult.toLowerCase()
          ? -1
          : b.reseller.financeResult.toLowerCase() > a.reseller.financeResult.toLowerCase()
          ? 1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.LEGAL_APPROVAL) {
      return array.sort((a, b) =>
        a.reseller.legalResult.toLowerCase() > b.reseller.legalResult.toLowerCase()
          ? -1
          : b.reseller.legalResult.toLowerCase() > a.reseller.legalResult.toLowerCase()
          ? 1
          : 0
      );
    } else if (sortColumn === COLUMN_NAMES.REGION) {
      return array.sort((a, b) =>
        a.reseller.region.toLowerCase() > b.reseller.region.toLowerCase()
          ? -1 
          : b.reseller.region.toLowerCase() > a.reseller.region.toLowerCase()
          ? 1
          : 0
      );
    } else {
      return array;
    }
  }
}, [sortPriority]); 

  const setSortColumnValue = (columnName) => {
    setPriorityChanged(true);
    setSortColumn(columnName);
    setFilteredResellers(sortRows(filteredResellers, columnName));
    if (sortPriority === "ASC") {
      setSortPriority("DESC");
    } else {
      setSortPriority("ASC");
    }
  };

  useEffect(() => {
    setSortPriority("ASC");
    setFilteredResellers(sortRows(filteredResellers, sortColumn));
  }, [sortColumn]);

  useEffect(() => {
    if (resellers.length > 0) {
      var filteredResellerList = [];
      const anyFormStatusChecked = Object.values(formStatusFilters).some(v => v);
      
      if (!anyFormStatusChecked) {
        filteredResellerList = resellers.filter(
          (res) =>
            res.status === LINK_STATUS.ACTIVE ||
            res.status === LINK_STATUS.EXPIRED
        );
      } else {
        if (formStatusFilters.signed) {
          filteredResellerList = resellers.filter(
            (res) =>
              res.reseller.formStatus === FORM_STATUS.SIGNED &&
              res.status === LINK_STATUS.ACTIVE
          );
        }

        if (formStatusFilters.completed) {
          filteredResellerList = resellers
            .filter(
              (res) =>
                (res.reseller.formStatus === FORM_STATUS.COMPLETED ||
                  res.reseller.formStatus === FORM_STATUS.EDIT_COMPLETED) &&
                res.status === LINK_STATUS.ACTIVE
            )
            .concat(filteredResellerList);
        }

        if (formStatusFilters.drafted) {
          filteredResellerList = resellers
            .filter((res) => res.reseller.formStatus === FORM_STATUS.DRAFTED && res.status === LINK_STATUS.ACTIVE)
            .concat(filteredResellerList);
        }

        if (formStatusFilters.pending) {
          filteredResellerList = resellers
            .filter((res) => res.reseller.formStatus === FORM_STATUS.ACTIVE && res.status === LINK_STATUS.ACTIVE)
            .concat(filteredResellerList);
        }

        if (formStatusFilters.requestedToEdit) {
          filteredResellerList = resellers
            .filter(
              (res) =>
                (res.reseller.formStatus === FORM_STATUS.TR_EDIT_REQUESTED ||
                  res.reseller.formStatus === FORM_STATUS.FULL_EDIT_REQUESTED) &&
                res.status === LINK_STATUS.ACTIVE
            )
            .concat(filteredResellerList);
        }

        if (formStatusFilters.expired) {
          filteredResellerList = resellers
            .filter((res) => res.status === LINK_STATUS.EXPIRED)
            .concat(filteredResellerList);
        }
      }
      setFilteredResellers(sortRows(filteredResellerList, sortColumn));

      // Filtering using Finance Approval
      var tempList = filteredResellerList;
      var filteredResellerList2 = [];

      const anyFinanceChecked = Object.values(financeApprovalFilters).some(v => v);

      if (!anyFinanceChecked) {
        filteredResellerList2 = tempList;
      } else {
        if (financeApprovalFilters.approved) {
          filteredResellerList2 = tempList.filter(
            (res) => res.reseller.financeResult === FINANCE_RESULT.APPROVED && res.status === LINK_STATUS.ACTIVE
          );
        }

        if (financeApprovalFilters.rejected) {
          filteredResellerList2 = tempList
            .filter(
              (res) =>
                res.reseller.financeResult === FINANCE_RESULT.REJECTED &&
                res.status === LINK_STATUS.ACTIVE &&
                !filteredResellerList2.includes(res)
            )
            .concat(filteredResellerList2);
        }

        if (financeApprovalFilters.pending) {
          filteredResellerList2 = tempList
            .filter(
              (res) =>
                res.reseller.financeResult === FINANCE_RESULT.PENDING &&
                res.status === LINK_STATUS.ACTIVE &&
                !filteredResellerList2.includes(res)
            )
            .concat(filteredResellerList2);
        }

        if (financeApprovalFilters.creatorPending) {
          filteredResellerList2 = tempList
            .filter(
              (res) =>
                res.reseller.financeResult === FINANCE_RESULT.CREATOR_PENDING &&
                res.status === LINK_STATUS.ACTIVE &&
                !filteredResellerList2.includes(res)
            )
            .concat(filteredResellerList2);
        }

        if (financeApprovalFilters.reviewerPending) {
          filteredResellerList2 = tempList
            .filter(
              (res) =>
                res.reseller.financeResult === FINANCE_RESULT.REVIEWER_PENDING &&
                res.status === LINK_STATUS.ACTIVE &&
                !filteredResellerList2.includes(res)
            )
            .concat(filteredResellerList2);
        }

        if (financeApprovalFilters.approvalPending) {
          filteredResellerList2 = tempList
            .filter(
              (res) =>
                res.reseller.financeResult === FINANCE_RESULT.APPROVAL_PENDING &&
                res.status === LINK_STATUS.ACTIVE &&
                !filteredResellerList2.includes(res)
            )
            .concat(filteredResellerList2);
        }
      }

      setFilteredResellers(sortRows(filteredResellerList2, sortColumn));

      // Filtering using Legal Approval
      tempList = filteredResellerList2;
      var filteredResellerList3 = [];

      const anyLegalChecked = Object.values(legalApprovalFilters).some(v => v);

      if (!anyLegalChecked) {
        filteredResellerList3 = tempList;
      } else {
        if (legalApprovalFilters.approved) {
          filteredResellerList3 = tempList.filter(
            (res) => res.reseller.legalResult === LEGAL_RESULT.APPROVED && res.status === LINK_STATUS.ACTIVE
          );
        }

        if (legalApprovalFilters.rejected) {
          filteredResellerList3 = tempList
            .filter(
              (res) =>
                res.reseller.legalResult === LEGAL_RESULT.REJECTED &&
                res.status === LINK_STATUS.ACTIVE &&
                !filteredResellerList3.includes(res)
            )
            .concat(filteredResellerList3);
        }

        if (legalApprovalFilters.pending) {
          filteredResellerList3 = tempList
            .filter(
              (res) =>
                res.reseller.legalResult === LEGAL_RESULT.PENDING &&
                res.status === LINK_STATUS.ACTIVE &&
                !filteredResellerList3.includes(res)
            )
            .concat(filteredResellerList3);
        }
      }

      // Filtering using Region
      var templist = filteredResellerList3;
      var filteredResellerList4 = [];

      const anyRegionSelected = Object.values(regionFilters).some(
        (value) => value === true
      );

      if (anyRegionSelected) {
        if (regionFilters.northAmerica) {
          filteredResellerList4 = filteredResellerList4.concat(
            templist.filter(
              (res) =>
                res.reseller.region?.toLowerCase() === REGION.NORTH_AMERICA &&
                !filteredResellerList4.includes(res)
            )
          );
        }

        if (regionFilters.latam) {
          filteredResellerList4 = filteredResellerList4.concat(
            templist.filter(
              (res) =>
                res.reseller.region?.toLowerCase() === REGION.LATAM &&
                !filteredResellerList4.includes(res)
            )
          );
        }

        if (regionFilters.middleEast) {
          filteredResellerList4 = filteredResellerList4.concat(
            templist.filter(
              (res) =>
                res.reseller.region?.toLowerCase() === REGION.MIDDLE_EAST &&
                !filteredResellerList4.includes(res)
            )
          );
        }

        if (regionFilters.anz) {
          filteredResellerList4 = filteredResellerList4.concat(
            templist.filter(
              (res) =>
                res.reseller.region?.toLowerCase() === REGION.ANZ &&
                !filteredResellerList4.includes(res)
            )
          );
        }

        if (regionFilters.uk) {
          filteredResellerList4 = filteredResellerList4.concat(
            templist.filter(
              (res) =>
                res.reseller.region?.toLowerCase() === REGION.UK &&
                !filteredResellerList4.includes(res)
            )
          );
        }

        if (regionFilters.eu) {
          filteredResellerList4 = filteredResellerList4.concat(
            templist.filter(
              (res) =>
                res.reseller.region?.toLowerCase() === REGION.EU &&
                !filteredResellerList4.includes(res)
            )
          );
        }

        if (regionFilters.asia) {
          filteredResellerList4 = filteredResellerList4.concat(
            templist.filter(
              (res) =>
                res.reseller.region?.toLowerCase() === REGION.ASIA &&
                !filteredResellerList4.includes(res)
            )
          );
        }

        if (regionFilters.africa) {
          filteredResellerList4 = filteredResellerList4.concat(
            templist.filter(
              (res) =>
                res.reseller.region?.toLowerCase() === REGION.AFRICA &&
                !filteredResellerList4.includes(res)
            )
          );
        }

        if (regionFilters.other) {
          filteredResellerList4 = filteredResellerList4.concat(
            templist.filter(
              (res) =>
                ![
                  REGION.NORTH_AMERICA,
                  REGION.LATAM,
                  REGION.MIDDLE_EAST,
                  REGION.ANZ,
                  REGION.UK,
                  REGION.EU,
                  REGION.ASIA,
                  REGION.AFRICA,
                ].includes(res.reseller.region?.toLowerCase()) &&
                !filteredResellerList4.includes(res)
            )
          );
        }

        setFilteredResellers(sortRows(filteredResellerList4, sortColumn));
      } else {
        setFilteredResellers(sortRows(templist, sortColumn));
      }
    }
  }, [
    formStatusFilters,
    financeApprovalFilters,
    legalApprovalFilters,
    regionFilters,
    resellers,
    sortColumn,
    sortRows,
  ]);

  const renderIndicator = (columnName) => {
    if (sortColumn === columnName && priorityChanged) {
      if (sortPriority === "ASC") {
        return <span style={{ fontSize: 20 }}>&#8595;</span>;
      } else {
        return <span style={{ fontSize: 20 }}>&#8593;</span>;
      }
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackBar({ visibility: false, severity: "", message: "" });
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditLinkId(null);
  }

  const renewResellerLink = async (id) => {
    setbackDrop(true);
    const requestURL = AppConfig.baseUrl + AppConfig.getResellers + "link/" + id + "/" + AppConfig.renewLink;
    try {
      let response = await fetch(requestURL, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + getAccessToken(),
        },
      });
      setbackDrop(false);
      if (response.status === 200) {
        setSnackBar({ visibility: true, severity: "success", message: "Reseller Link Renewed Successfully" });
        setFormRender(formRender + 1);
      } else {
        setSnackBar({ visibility: true, severity: "error", message: "Something went wrong while renewing link" });
      }
    } catch (error) {
      setbackDrop(false);
      setSnackBar({ visibility: true, severity: "error", message: "Something went wrong while renewing link" });
      console.error(error);
    }
  }

  const copyURL = async (item) => {
    const linkURL = AppConfig.externalUrl + "resellerform/validate/" + item.encodeString;
    try {
      await navigator.clipboard.writeText(linkURL);
      setCopy(true);
      setSnackBar({ visibility: true, severity: "success", message: "Copied to Clipboard" });
    } catch (err) {
      setSnackBar({ visibility: true, severity: "error", message: "Failed to copy to Clipboard" });
    }
  };  

  const editResellerLink = async (id, companyName, emails, contactName, channelManagerEmail) => {
    setEditDialogOpen(true);
    setEditLinkId(id);
    setEditCompanyName(companyName);
    setEditEmails(emails);
    setEditContactName(contactName);
    setEditChannelManagerEmail(channelManagerEmail);
  }

  const getActiveFilterLabels = () => {
    const labels = [];

    if (formStatusFilters.signed)
      labels.push({
        category: "formStatus",
        label: "Status: Completed",
        key: "signed",
      });
    if (formStatusFilters.completed)
      labels.push({
        category: "formStatus",
        label: "Status: Pending Signature",
        key: "completed",
      });
    if (formStatusFilters.drafted)
      labels.push({
        category: "formStatus",
        label: "Status: Drafted",
        key: "drafted",
      });
    if (formStatusFilters.pending)
      labels.push({
        category: "formStatus",
        label: "Status: Pending",
        key: "pending",
      });
    if (formStatusFilters.requestedToEdit)
      labels.push({
        category: "formStatus",
        label: "Status: Requested to Edit",
        key: "requestedToEdit",
      });
    if (formStatusFilters.expired)
      labels.push({
        category: "formStatus",
        label: "Status: Expired",
        key: "expired",
      });

    if (financeApprovalFilters.approved)
      labels.push({
        category: "financeApproval",
        label: "Finance: Approved",
        key: "approved",
      });
    if (financeApprovalFilters.rejected)
      labels.push({
        category: "financeApproval",
        label: "Finance: Rejected",
        key: "rejected",
      });
    if (financeApprovalFilters.pending)
      labels.push({
        category: "financeApproval",
        label: "Finance: Pending",
        key: "pending",
      });
    if (financeApprovalFilters.creatorPending)
      labels.push({
        category: "financeApproval",
        label: "Finance: Creator Pending",
        key: "creatorPending",
      });
    if (financeApprovalFilters.reviewerPending)
      labels.push({
        category: "financeApproval",
        label: "Finance: Reviewer Pending",
        key: "reviewerPending",
      });
    if (financeApprovalFilters.approvalPending)
      labels.push({
        category: "financeApproval",
        label: "Finance: Approval Pending",
        key: "approvalPending",
      });

    if (legalApprovalFilters.approved)
      labels.push({
        category: "legalApproval",
        label: "Legal: Approved",
        key: "approved",
      });
    if (legalApprovalFilters.rejected)
      labels.push({
        category: "legalApproval",
        label: "Legal: Rejected",
        key: "rejected",
      });
    if (legalApprovalFilters.pending)
      labels.push({
        category: "legalApproval",
        label: "Legal: Pending",
        key: "pending",
      });

    if (regionFilters.northAmerica)
      labels.push({
        category: "region",
        label: "Region: North America",
        key: "northAmerica",
      });
    if (regionFilters.latam)
      labels.push({ category: "region", label: "Region: LATAM", key: "latam" });
    if (regionFilters.middleEast)
      labels.push({
        category: "region",
        label: "Region: Middle East",
        key: "middleEast",
      });
    if (regionFilters.anz)
      labels.push({ category: "region", label: "Region: ANZ", key: "anz" });
    if (regionFilters.uk)
      labels.push({ category: "region", label: "Region: UK", key: "uk" });
    if (regionFilters.eu)
      labels.push({ category: "region", label: "Region: EU", key: "eu" });
    if (regionFilters.asia)
      labels.push({ category: "region", label: "Region: Asia", key: "asia" });
    if (regionFilters.africa)
      labels.push({
        category: "region",
        label: "Region: Africa",
        key: "africa",
      });
    if (regionFilters.other)
      labels.push({ category: "region", label: "Region: Other", key: "other" });

    return labels;
  };

  const removeActiveFilter = (category, key) => {
    switch (category) {
      case 'formStatus':
        handleFormStatusFilters(key);
        break;
      case 'financeApproval':
        handleFinanceApprovalFilter(key);
        break;
      case 'legalApproval':
        handleLegalApprovalHandler(key);
        break;
      case 'region':
        handleRegionFilter(key);
        break;
      default:
        break;  
    }
  };

  const handleActionMenuOpen = (event, item) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setSelectedItem(item);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedItem(null);
  };

  const handleRenewClick = () => {
    if (selectedItem) {
      renewResellerLink(selectedItem.reseller.linkId);
    }
    handleActionMenuClose();
  };

  const handleCopyClick = () => {
    if (selectedItem) {
      copyURL(selectedItem);
    }
    handleActionMenuClose();
  };

  const handleEditClick = () => {
    if (selectedItem) {
      editResellerLink(
        selectedItem.reseller.linkId,
        selectedItem.reseller.companyName,
        selectedItem.emails,
        selectedItem.contactName,
        selectedItem.channelManagerEmail
      );
    }
    handleActionMenuClose();
  };
          
  return (
    <div>
      <Backdrop open={backdropOpen} className={classes.backdrop}>
        <CircularProgress color="secondary" />
      </Backdrop>

      <Snackbar
        open={snack.visibility}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={snack.severity}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Grid
        container
        spacing={1}
        alignItems="flex-end"
        className={classes.searchGrid}
      >
        <Grid item xs={12} sm="auto">
          {(checkIfRoleExists(roles.admin) ||
            checkIfRoleExists(roles.channelManager) ||
            checkIfRoleExists(roles.finance) ||
            checkIfRoleExists(roles.legal)) && (
            <Button
              className={classes.addButton}
              variant="contained"
              color="secondary"
              onClick={openDialog}
              startIcon={<AddCircleOutlineIcon />}
            >
              Request Due Diligence
            </Button>
          )}
        </Grid>
        <Grid item xs={12} sm="auto" className={classes.checkBox}>
          <FormControlLabel
            className={classes.checkBox}
            control={
              <Checkbox
                className={classes.checkBox}
                size="small"
                checked={isInactive}
                onChange={handleCheckbox}
                name="isInactive"
                color="Secondary"
              />
            }
            label={
              <span style={{ fontSize: 13 }}>Show Inactive Companies</span>
            }
          />
        </Grid>
        <Grid item xs={12} sm>
          <Paper component="form" className={classes.root}>
            <InputBase
              className={classes.input}
              placeholder="Search"
              onChange={handleSearch}
            />
            <SearchIcon />
          </Paper>
        </Grid>
        <Grid item xs={12} sm="auto">
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            className={classes.downloadButton}
            onClick={handleDownloadCsv}
          >
            Download CSV
          </Button>
        </Grid>
      </Grid>

      {!isInactive && (
        <Accordion expanded={true}>
          <AccordionSummary className={classes.accordionSummary}>
            <Typography className={classes.accordionHeading}>
              Search Filters
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <Box className={classes.filterContainer}>
              <Button
                className={classes.filterButton}
                onClick={(e) => setFormStatusAnchor(e.currentTarget)}
                endIcon={<ExpandMoreIcon />}
              >
                Form Status
              </Button>
              <Button
                className={classes.filterButton}
                onClick={(e) => setFinanceApprovalAnchor(e.currentTarget)}
                endIcon={<ExpandMoreIcon />}
              >
                Finance Approval
              </Button>
              <Button
                className={classes.filterButton}
                onClick={(e) => setLegalApprovalAnchor(e.currentTarget)}
                endIcon={<ExpandMoreIcon />}
              >
                Legal Approval
              </Button>
              <Button
                className={classes.filterButton}
                onClick={(e) => setRegionAnchor(e.currentTarget)}
                endIcon={<ExpandMoreIcon />}
              >
                Region
              </Button>
            </Box>
            {getActiveFilterLabels().length > 0 && (
              <Box style={{ marginBottom: "2px", textAlign: "left" }}>
                {getActiveFilterLabels().map((filter, index) => (
                  <span key={index} className={classes.activeFilterChip}>
                    {filter.label}
                    <CloseIcon
                      className={classes.activeFilterChipClose}
                      onClick={() =>
                        removeActiveFilter(filter.category, filter.key)
                      }
                      style={{ fontSize: "16px" }}
                    />
                  </span>
                ))}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {!isInactive && (
        <>
          <Menu
            anchorEl={formStatusAnchor}
            open={Boolean(formStatusAnchor)}
            onClose={() => setFormStatusAnchor(null)}
            className={classes.filterMenu}
            PaperProps={{
              className: classes.filterMenuPaper,
            }}
          >
            <MenuItem
              onClick={() => handleFormStatusFilters(FILTERS.SIGNED)}
              className={classes.menuItem}
            >
              <Checkbox checked={formStatusFilters.signed} size="small" />
              <ListItemText primary="Completed" />
            </MenuItem>
            <MenuItem
              onClick={() => handleFormStatusFilters(FILTERS.COMPLETED)}
              className={classes.menuItem}
            >
              <Checkbox checked={formStatusFilters.completed} size="small" />
              <ListItemText primary="Pending Signature" />
            </MenuItem>
            <MenuItem
              onClick={() => handleFormStatusFilters("drafted")}
              className={classes.menuItem}
            >
              <Checkbox checked={formStatusFilters.drafted} size="small" />
              <ListItemText primary="Drafted" />
            </MenuItem>
            <MenuItem
              onClick={() => handleFormStatusFilters(FILTERS.PENDING)}
              className={classes.menuItem}
            >
              <Checkbox checked={formStatusFilters.pending} size="small" />
              <ListItemText primary="Pending" />
            </MenuItem>
            <MenuItem
              onClick={() => handleFormStatusFilters(FILTERS.REQUESTED_TO_EDIT)}
              className={classes.menuItem}
            >
              <Checkbox
                checked={formStatusFilters.requestedToEdit}
                size="small"
              />
              <ListItemText primary="Requested to Edit" />
            </MenuItem>
            <MenuItem
              onClick={() => handleFormStatusFilters(FILTERS.EXPIRED)}
              className={classes.menuItem}
            >
              <Checkbox checked={formStatusFilters.expired} size="small" />
              <ListItemText primary="Expired" />
            </MenuItem>
          </Menu>

          <Menu
            anchorEl={financeApprovalAnchor}
            open={Boolean(financeApprovalAnchor)}
            onClose={() => setFinanceApprovalAnchor(null)}
            className={classes.filterMenu}
            PaperProps={{
              className: classes.filterMenuPaper,
            }}
          >
            <MenuItem
              onClick={() => handleFinanceApprovalFilter(FILTERS.APPROVED)}
              className={classes.menuItem}
            >
              <Checkbox
                checked={financeApprovalFilters.approved}
                size="small"
              />
              <ListItemText primary="Approved" />
            </MenuItem>
            <MenuItem
              onClick={() => handleFinanceApprovalFilter(FILTERS.REJECTED)}
              className={classes.menuItem}
            >
              <Checkbox
                checked={financeApprovalFilters.rejected}
                size="small"
              />
              <ListItemText primary="Rejected" />
            </MenuItem>
            <MenuItem
              onClick={() => handleFinanceApprovalFilter(FILTERS.PENDING)}
              className={classes.menuItem}
            >
              <Checkbox checked={financeApprovalFilters.pending} size="small" />
              <ListItemText primary="Pending" />
            </MenuItem>
            <MenuItem
              onClick={() => handleFinanceApprovalFilter(FILTERS.CREATOR_PENDING)}
              className={classes.menuItem}
            >
              <Checkbox checked={financeApprovalFilters.creatorPending} size="small" />
              <ListItemText primary="Creator Pending" />
            </MenuItem>
            <MenuItem
              onClick={() => handleFinanceApprovalFilter(FILTERS.REVIEWER_PENDING)}
              className={classes.menuItem}
            >
              <Checkbox checked={financeApprovalFilters.reviewerPending} size="small" />
              <ListItemText primary="Reviewer Pending" />
            </MenuItem>
            <MenuItem
              onClick={() => handleFinanceApprovalFilter(FILTERS.APPROVAL_PENDING)}
              className={classes.menuItem}
            >
              <Checkbox checked={financeApprovalFilters.approvalPending} size="small" />
              <ListItemText primary="Approval Pending" />
            </MenuItem>
          </Menu>
          <Menu
            anchorEl={legalApprovalAnchor}
            open={Boolean(legalApprovalAnchor)}
            onClose={() => setLegalApprovalAnchor(null)}
            className={classes.filterMenu}
            PaperProps={{
              className: classes.filterMenuPaper,
            }}
          >
            <MenuItem
              onClick={() => handleLegalApprovalHandler(FILTERS.APPROVED)}
              className={classes.menuItem}
            >
              <Checkbox checked={legalApprovalFilters.approved} size="small" />
              <ListItemText primary="Approved" />
            </MenuItem>
            <MenuItem
              onClick={() => handleLegalApprovalHandler(FILTERS.REJECTED)}
              className={classes.menuItem}
            >
              <Checkbox checked={legalApprovalFilters.rejected} size="small" />
              <ListItemText primary="Rejected" />
            </MenuItem>
            <MenuItem
              onClick={() => handleLegalApprovalHandler(FILTERS.PENDING)}
              className={classes.menuItem}
            >
              <Checkbox checked={legalApprovalFilters.pending} size="small" />
              <ListItemText primary="Pending" />
            </MenuItem>
          </Menu>
          <Menu
            anchorEl={regionAnchor}
            open={Boolean(regionAnchor)}
            onClose={() => setRegionAnchor(null)}
            className={classes.filterMenu}
            PaperProps={{
              className: classes.filterMenuPaper,
            }}
          >
            <MenuItem
              onClick={() => handleRegionFilter(FILTERS.NORTH_AMERICA)}
              className={classes.menuItem}
            >
              <Checkbox checked={regionFilters.northAmerica} size="small" />
              <ListItemText primary="North America" />
            </MenuItem>
            <MenuItem
              onClick={() => handleRegionFilter(FILTERS.LATAM)}
              className={classes.menuItem}
            >
              <Checkbox checked={regionFilters.latam} size="small" />
              <ListItemText primary="LATAM" />
            </MenuItem>
            <MenuItem
              onClick={() => handleRegionFilter("middleEast")}
              className={classes.menuItem}
            >
              <Checkbox checked={regionFilters.middleEast} size="small" />
              <ListItemText primary="Middle East" />
            </MenuItem>
            <MenuItem
              onClick={() => handleRegionFilter(FILTERS.ANZ)}
              className={classes.menuItem}
            >
              <Checkbox checked={regionFilters.anz} size="small" />
              <ListItemText primary="ANZ" />
            </MenuItem>
            <MenuItem
              onClick={() => handleRegionFilter(FILTERS.UK)}
              className={classes.menuItem}
            >
              <Checkbox checked={regionFilters.uk} size="small" />
              <ListItemText primary="UK" />
            </MenuItem>
            <MenuItem
              onClick={() => handleRegionFilter(FILTERS.EU)}
              className={classes.menuItem}
            >
              <Checkbox checked={regionFilters.eu} size="small" />
              <ListItemText primary="EU" />
            </MenuItem>
            <MenuItem
              onClick={() => handleRegionFilter(FILTERS.ASIA)}
              className={classes.menuItem}
            >
              <Checkbox checked={regionFilters.asia} size="small" />
              <ListItemText primary="Asia" />
            </MenuItem>
            <MenuItem
              onClick={() => handleRegionFilter(FILTERS.AFRICA)}
              className={classes.menuItem}
            >
              <Checkbox checked={regionFilters.africa} size="small" />
              <ListItemText primary="Africa" />
            </MenuItem>
            <MenuItem
              onClick={() => handleRegionFilter(FILTERS.OTHER)}
              className={classes.menuItem}
            >
              <Checkbox checked={regionFilters.other} size="small" />
              <ListItemText primary="Other" />
            </MenuItem>
          </Menu>

          {/* Action Menu Pop Up */}
          <Menu
            anchorEl={actionMenuAnchor}
            open={Boolean(actionMenuAnchor)}
            onClose={handleActionMenuClose}
            aria-label="Actions Menu"
            PaperProps={{
              className: classes.actionMenuPaper,
            }}
          >
            <MenuItem onClick={handleRenewClick} className={classes.actionMenuItem}>
              <CachedIcon className={classes.actionMenuIcon} />
              <span className={classes.actionMenuText}>Renew Link</span>
            </MenuItem>
            <MenuItem onClick={handleCopyClick} className={classes.actionMenuItem}>
              <FileCopyIcon className={classes.actionMenuIcon} />
              <span className={classes.actionMenuText}>Copy Link</span>
            </MenuItem>
            {selectedItem?.reseller.formStatus === FORM_STATUS.ACTIVE && 
             selectedItem?.status !== LINK_STATUS.EXPIRED ? (
              <MenuItem onClick={handleEditClick} className={classes.actionMenuItem}>
                <EditIcon className={classes.actionMenuIcon} />
                <span className={classes.actionMenuText}>Edit Details</span>
              </MenuItem>
            ) : (
              <MenuItem disabled className={classes.actionMenuItem}>
                <EditIcon className={classes.actionMenuIcon}/>
                <span className={classes.actionMenuText}>Edit Details</span>
              </MenuItem>
            )}
          </Menu>
        </>
      )}

      {!backdropOpen && (
        <Fragment>
          <TableContainer className={classes.tableContainer} component={Paper}>
            <Table className={classes.table} aria-label="simple table">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell
                    align="center"
                    className={classes.column}
                    onClick={() => setSortColumnValue(COLUMN_NAMES.COMPANY_NAME)}
                  >
                    Company Name {renderIndicator(COLUMN_NAMES.COMPANY_NAME)}
                  </TableCell>
                  <TableCell
                    align="center"
                    className={classes.column}
                    onClick={() => setSortColumnValue(COLUMN_NAMES.CONTACT_EMAIL)}
                  >
                    Contact Email {renderIndicator(COLUMN_NAMES.CONTACT_EMAIL)}
                  </TableCell>
                  <TableCell
                    align="center"
                    className={classes.column}
                    onClick={() =>
                      setSortColumnValue(COLUMN_NAMES.CHANNEL_MANAGER_EMAIL)
                    }
                  >
                    Channel Manager Email
                    {renderIndicator(COLUMN_NAMES.CHANNEL_MANAGER_EMAIL)}
                  </TableCell>
                  <TableCell
                    align="center"
                    className={classes.column}
                    onClick={() => setSortColumnValue(COLUMN_NAMES.COUNTRY)}
                  >
                    Country {renderIndicator(COLUMN_NAMES.COUNTRY)}
                  </TableCell>
                  <TableCell
                    align="center"
                    className={classes.column}
                    onClick={() => setSortColumnValue(COLUMN_NAMES.REGION)}
                  >
                    Region {renderIndicator(COLUMN_NAMES.REGION)}
                  </TableCell>
                  <TableCell
                    align="center"
                    className={classes.column}
                    style={{ minWidth: "100px" }}
                  >
                    Form Status
                  </TableCell>
                  <TableCell
                    align="center"
                    className={classes.column}
                    style={{ minWidth: "120px" }}
                  >
                    Finance Approval
                  </TableCell>
                  <TableCell
                    align="center"
                    className={classes.column}
                    style={{ minWidth: "100px" }}
                  >
                    Legal Approval
                  </TableCell>
                  <TableCell align="center" className={classes.column}>Action Icons</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredResellers.length > 0 &&
                  filteredResellers
                    .filter(
                      (item) =>
                        (item.reseller.companyName
                          ? item.reseller.companyName.toLowerCase().includes(searchInput.toLowerCase())
                          : item.companyName.toLowerCase().includes(searchInput.toLowerCase())) ||
                          item.emails.some(email => email.toLowerCase().includes(searchInput.toLowerCase())) ||
                        item.reseller.country.toLowerCase().includes(searchInput.toLowerCase())
                    )
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item, index) => (
                      <TableRow key={index} onClick={() => openSummary(item)} className={
                        item.reseller.formStatus === FORM_STATUS.ACTIVE && item.status === LINK_STATUS.EXPIRED
                          ? classes.tableRowExpired
                          : classes.tableRow1
                      }>
                        <TableCell align="center" className={classes.plainText}>
                          {item.reseller.companyName || item.companyName}
                        </TableCell>
                        <TableCell align="center" className={classes.plainText}>{item.emails.join(', ')}</TableCell>
                        <TableCell align="center" className={classes.plainText}>{item.channelManagerEmail}</TableCell>
                        <TableCell align="center" className={classes.plainText}>{item.reseller.country}</TableCell>
                        <TableCell align="center" className={classes.plainText}>{item.reseller.region || "-"}</TableCell>
                        <TableCell align="center" className={classes.plainText}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <div className={classes.resultChip}>
                              {getFormStatusLabel(item.reseller.formStatus, item.status)}
                            </div>
                            {item.reseller.formStatus === FORM_STATUS.SIGNED &&
                              item.reseller.signedDocumentUpdatedOn && (
                              <Typography className={classes.dateText}>
                                {new Date(item.reseller.signedDocumentUpdatedOn).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell style={{ textTransform: "capitalize" }} className={classes.plainText} align="center">
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <div className={classes.resultChip}>
                              {item.reseller.financeResult}
                            </div>
                            {(item.reseller.financeResult === FINANCE_RESULT.APPROVED ||
                              item.reseller.financeResult === FINANCE_RESULT.REJECTED) &&
                              item.reseller.financeResultUpdatedOn && (
                              <Typography className={classes.dateText}>
                                {new Date(item.reseller.financeResultUpdatedOn).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell style={{ textTransform: "capitalize" }} className={classes.plainText} align="center">
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <div className={classes.resultChip}>
                              {item.reseller.legalResult}
                            </div>
                            {(item.reseller.legalResult ===
                              LEGAL_RESULT.APPROVED ||
                              item.reseller.legalResult ===
                                LEGAL_RESULT.REJECTED) &&
                              item.reseller.legalResultUpdatedOn && (
                              <Typography className={classes.dateText}>
                                {new Date(item.reseller.legalResultUpdatedOn).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center" className={classes.plainText}>
                          <Tooltip title="Show Actions">
                            <IconButton
                              onClick={(e) => handleActionMenuOpen(e, item)}
                            >
                              <MoreVertIcon
                                color="primary"
                                className={classes.actionButton}
                              />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredResellers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Fragment>
      )}

      {!backdropOpen &&
        filteredResellers.filter(
          (item) =>
            (item.reseller.companyName
              ? item.reseller.companyName.toLowerCase().includes(searchInput.toLowerCase())
              : item.companyName.toLowerCase().includes(searchInput.toLowerCase())) ||
              item.emails.some(email => email.toLowerCase().includes(searchInput.toLowerCase())) ||
            item.reseller.country.toLowerCase().includes(searchInput.toLowerCase())
        ).length === 0 && (
          <Typography className={classes.msgTypo} variant="body2">
            No Resellers Found
          </Typography>
        )}

      {dialogOpen && (
        <NewResellerDialog
          dialogOpen={dialogOpen}
          handleDialogClose={handleDialogClose}
          setSnackBar={setSnackBar}
          setFormRender={setFormRender}
        />
      )}
      {editDialogOpen && (
        <EditResellerDialog
          dialogOpen={editDialogOpen}
          handleDialogClose={handleEditDialogClose}
          setSnackBar={setSnackBar}
          setFormRender={setFormRender}
          linkId={editLinkId}
          companyName={editCompanyName}
          emails={editEmails}
          contactName={editContactName}
          channelManagerEmail={editChannelManagerEmail}
        />
      )}
    </div>
  );
};

export default Resellers;
